import {
  Button,
  ButtonGroup,
  Form,
  Fieldset,
  TextInput,
  fieldKey,
  useControlled,
  Select,
  ListBoxItem,
} from "@datapay-by-datacom/design-system";
import {
  OrgCategory,
  OrgInvitationCreateRole,
  OrgInvitationCreateDeliveryMethod,
  ManualCreateInvitationResponse,
  OrgInvitationCreate,
} from "../../../generated/core-api";
import { useCreateOrgInvitation } from "../../hooks";

interface OrgInviteCreateFormProps {
  category: OrgCategory;
  id: string;
  onSuccess: (link: string) => void;
  onCancel: () => void;
}

export function OrgInviteCreateForm({
  category,
  id,
  onSuccess,
  onCancel,
}: OrgInviteCreateFormProps) {
  const createInviteMutation = useCreateOrgInvitation();

  const field = useControlled({
    role: "member" as OrgInvitationCreateRole,
    recipient_email: "",
    recipient_mobile: "",
    recipient_name: "",
    delivery_method: "manual" as OrgInvitationCreateDeliveryMethod,
  } satisfies Omit<OrgInvitationCreate, "org_id" | "org_category" | "invitation_id">);

  const input = fieldKey(field);

  // Helper to determine if email fields should be required
  const isEmailRequired = field.value.delivery_method === "email";

  // Helper to determine if mobile fields should be required
  const isMobileRequired = field.value.delivery_method === "sms";

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    // Create a copy of the field value for submission
    const formData = {
      ...field.value,
      // Convert empty strings to null for optional fields
      recipient_email: field.value.recipient_email || null,
      recipient_mobile: field.value.recipient_mobile || null,
      recipient_name: field.value.recipient_name || null,
    };

    createInviteMutation.mutate(
      {
        ...formData,
        invitation_id: crypto.randomUUID().toString(),
        org_id: id,
        org_category: category,
      },
      {
        onSuccess: (response) => {
          // Get the one_time_link from the API response
          if ("one_time_link" in response) {
            const link = (response as ManualCreateInvitationResponse)
              .one_time_link;
            onSuccess(link);
          }
        },
      }
    );
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Fieldset>
        <Select
          label="Delivery Method"
          isRequired
          selectedKey={field.value.delivery_method}
          onSelectionChange={(value) =>
            input("delivery_method").onChange(
              value as OrgInvitationCreateDeliveryMethod
            )
          }
        >
          <ListBoxItem id="manual">Shareable Link</ListBoxItem>
          <ListBoxItem id="email" isDisabled>
            Email (Coming Soon)
          </ListBoxItem>
          <ListBoxItem id="sms" isDisabled>
            SMS (Coming Soon)
          </ListBoxItem>
        </Select>

        <TextInput
          label="Recipient Email"
          placeholder="Enter recipient email"
          {...input("recipient_email")}
          isRequired={isEmailRequired}
        />

        <TextInput
          label="Recipient Mobile"
          placeholder="Enter recipient mobile number"
          {...input("recipient_mobile")}
          isRequired={isMobileRequired}
        />

        <TextInput
          label="Recipient Name"
          placeholder="Enter recipient name"
          {...input("recipient_name")}
        />

        <Select
          label="Role"
          isRequired
          selectedKey={field.value.role}
          onSelectionChange={(value) =>
            input("role").onChange(value as OrgInvitationCreateRole)
          }
        >
          <ListBoxItem id="member">Member</ListBoxItem>
          <ListBoxItem id="admin">Admin</ListBoxItem>
        </Select>

        <ButtonGroup align="right">
          <Button variant="tertiary" onPress={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            isDisabled={
              createInviteMutation.isPending ||
              field.value.delivery_method === "email" ||
              field.value.delivery_method === "sms"
            }
          >
            {createInviteMutation.isPending
              ? "Creating..."
              : "Create Invitation"}
          </Button>
        </ButtonGroup>
      </Fieldset>
    </Form>
  );
}
